import { EXPERIENCE_IDS } from '@/components/admin/experiences/contracts';
import { FilterField, filterControlClass } from '@/components/admin/versioned/filter-field';
import { LINK_FILTER_LABEL } from '@/lib/experience-labels';
import type { ExperienceFilters, ExperienceLinkFilter } from '@matter_extractor/shared';
import { experienceLinkFilterSchema } from '@matter_extractor/shared';
import { useId } from 'react';

/**
 * The three own filters of an experience (refonte modulaire §15; maquette
 * `Main.dc.html`).
 *
 * Two of them - the protocol and the persona - sit in the ALWAYS-VISIBLE row,
 * in the two slots the layer keeps for its referent filters, and the third
 * sits in « Plus de filtres » with the common ones. That split is the mockup's
 * and it is argued in `filter-bar.tsx` at `renderReferents`: an experience's
 * referents are the links of §5.1, so « Protocole : nulle part » and an
 * « Expérience » select would both be filters on a relation this screen cannot
 * have - while the two references it CARRIES are exactly what §15 names
 * visible.
 *
 * ## The options come from the CARDS, and they are names
 *
 * `experienceCardSchema` names the protocol and the persona of each experience
 * and carries neither id, so the value of these two selects is a NAME and the
 * option list is the distinct names the library holds. The consequence - two
 * protocols of one name conflated - is named in `experience-filters.ts`, where
 * the comparison lives.
 *
 * A select whose options are the library's own names rather than « Tous » and
 * « Nulle part » is also what makes them useful on day one: the referent
 * selects of the layer admit they have two states because no route serves their
 * list; here the list is the list already on screen.
 */

export interface ExperienceReferenceFiltersProps {
  filters: ExperienceFilters;
  /** The distinct names the library holds, sorted - `experienceReferenceOptions`. */
  options: { readonly protocols: readonly string[]; readonly personas: readonly string[] };
  onChange: (patch: Partial<ExperienceFilters>) => void;
}

export function ExperienceReferenceFilters({
  filters,
  options,
  onChange,
}: ExperienceReferenceFiltersProps) {
  const protocolId = useId();
  const personaId = useId();

  return (
    <>
      <FilterField label="Protocole" htmlFor={protocolId} className="w-45">
        <select
          id={protocolId}
          data-testid={EXPERIENCE_IDS.filterProtocol}
          value={filters.referencedProtocolId ?? ''}
          onChange={(event) =>
            onChange({
              referencedProtocolId:
                event.currentTarget.value === '' ? null : event.currentTarget.value,
            })
          }
          className={filterControlClass(filters.referencedProtocolId !== null, 'w-full')}
        >
          <option value="">Tous</option>
          {options.protocols.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
      </FilterField>

      <FilterField label="Persona" htmlFor={personaId} className="w-45">
        <select
          id={personaId}
          data-testid={EXPERIENCE_IDS.filterPersona}
          value={filters.referencedPersonaId ?? ''}
          onChange={(event) =>
            onChange({
              referencedPersonaId:
                event.currentTarget.value === '' ? null : event.currentTarget.value,
            })
          }
          className={filterControlClass(filters.referencedPersonaId !== null, 'w-full')}
        >
          <option value="">Tous</option>
          {options.personas.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
      </FilterField>
    </>
  );
}

export interface ExperienceLinkFilterFieldProps {
  filters: ExperienceFilters;
  onChange: (patch: Partial<ExperienceFilters>) => void;
}

/** « Lien actif » - the own filter of the « Plus de filtres » panel (§15). */
export function ExperienceLinkFilterField({ filters, onChange }: ExperienceLinkFilterFieldProps) {
  const linkId = useId();
  return (
    <FilterField label="Lien actif" htmlFor={linkId} className="w-45">
      <select
        id={linkId}
        data-testid={EXPERIENCE_IDS.filterLink}
        value={filters.activeLink}
        onChange={(event) =>
          onChange({ activeLink: event.currentTarget.value as ExperienceLinkFilter })
        }
        className={filterControlClass(filters.activeLink !== 'all', 'w-full')}
      >
        {experienceLinkFilterSchema.options.map((option) => (
          <option key={option} value={option}>
            {LINK_FILTER_LABEL[option]}
          </option>
        ))}
      </select>
    </FilterField>
  );
}
